import PageNavbar from '../../components/feature/PageNavbar';
import { useNavigate } from 'react-router-dom';

export default function NeuroplasticityPage() {
  const navigate = useNavigate();
  
  const corePrinciples = [
    { title: 'Use It or Lose It', desc: 'Neural pathways weaken without regular use' },
    { title: 'Use It and Improve It', desc: 'Practice strengthens neural connections' },
    { title: 'Specificity', desc: 'Training must be task-specific for best results' },
    { title: 'Repetition Matters', desc: 'High repetition drives lasting neural change' },
    { title: 'Intensity Matters', desc: 'Sufficient intensity is needed to induce plasticity' },
    { title: 'Time Matters', desc: 'Different plasticity occurs at different stages' },
    { title: 'Salience Matters', desc: 'Meaningful activities enhance learning' },
    { title: 'Age Matters', desc: 'Plasticity occurs across the lifespan' },
    { title: 'Transference', desc: 'Skills can transfer to related activities' },
    { title: 'Interference', desc: 'Some experiences can impede plasticity' }
  ];

  const technologies = [
    {
      title: 'Robotics',
      desc: 'Robotic devices provide high-repetition, task-specific training with real-time feedback to promote motor learning.',
      image: 'https://public.readdy.ai/ai/img_res/6c813469c9a0a0c63bb2b6edc501f020.jpg'
    },
    {
      title: 'Virtual Reality',
      desc: 'Immersive VR environments create engaging, task-specific practice opportunities that enhance motivation and neuroplastic change.',
      image: 'https://readdy.ai/api/search-image?query=Patient%20wearing%20virtual%20reality%20headset%20during%20rehabilitation%20therapy%20session%20in%20modern%20clinic%20immersive%20VR%20treatment%20for%20neurological%20recovery%20clean%20medical%20environment&width=400&height=250&seq=neuro002&orientation=landscape'
    },
    {
      title: 'Artificial Intelligence',
      desc: 'AI algorithms analyze movement patterns and adapt therapy in real-time to optimize neuroplastic outcomes.',
      image: 'https://readdy.ai/api/search-image?query=Artificial%20intelligence%20brain%20scan%20analysis%20on%20medical%20monitor%20showing%20neural%20pathways%20and%20connections%20modern%20healthcare%20technology%20digital%20brain%20mapping%20visualization&width=400&height=250&seq=neuro003&orientation=landscape'
    },
    {
      title: 'FES (Functional Electrical Stimulation)',
      desc: 'Targeted electrical stimulation activates muscles and nerves to reinforce motor pathways and enhance functional recovery.',
      image: 'https://readdy.ai/api/search-image?query=Functional%20electrical%20stimulation%20therapy%20electrodes%20on%20patient%20arm%20in%20rehabilitation%20clinic%20professional%20medical%20treatment%20for%20muscle%20activation%20clean%20clinical%20setting&width=400&height=250&seq=neuro004&orientation=landscape'
    },
    {
      title: 'Exoskeleton',
      desc: 'Powered exoskeletons enable intensive gait training with proper biomechanics to drive lower limb neuroplasticity.',
      image: 'https://readdy.ai/api/search-image?query=Patient%20using%20robotic%20exoskeleton%20for%20walking%20rehabilitation%20in%20modern%20therapy%20center%20wearable%20robotic%20suit%20for%20mobility%20training%20professional%20healthcare%20environment&width=400&height=250&seq=neuro005&orientation=landscape'
    },
    {
      title: 'Gamification',
      desc: 'Game-based therapy increases engagement and motivation, key factors in driving neuroplastic change through enjoyable repetition.',
      image: 'https://readdy.ai/api/search-image?query=Interactive%20gamified%20rehabilitation%20exercise%20on%20large%20screen%20patient%20playing%20therapeutic%20video%20game%20in%20modern%20clinic%20fun%20engaging%20physical%20therapy%20technology&width=400&height=250&seq=neuro006&orientation=landscape'
    }
  ];

  const practiceAreas = [
    {
      title: 'Movement Retraining',
      desc: 'Repetitive, task-specific exercises to rebuild motor pathways and restore functional movement patterns.'
    },
    {
      title: 'High-Repetition Training',
      desc: 'Intensive practice protocols designed to maximize neural adaptation through thousands of movement repetitions.'
    },
    {
      title: 'Meaningful Tasks',
      desc: 'Therapy focused on activities that matter to you, enhancing salience and accelerating neuroplastic change.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-teal-50 via-white to-cyan-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-teal-600 font-medium mb-2">The Brain&apos;s Superpower</p>
              <h1 className="text-4xl font-bold text-gray-900 mb-6">What is Neuroplasticity?</h1>
              <p className="text-gray-600 leading-relaxed mb-6">
                Your brain is remarkably adaptable — a property called <strong className="text-teal-600">neuroplasticity</strong>. It means your brain can reorganize itself by forming new neural connections throughout life.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Neuroplasticity allows neurons (nerve cells) in the brain to compensate for injury and disease and to adjust their activities in response to new situations or changes in their environment.
              </p>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-teal-100">
                <p className="font-semibold text-gray-900 mb-2">Key Insight</p>
                <p className="text-sm text-gray-600">The key to harnessing neuroplasticity is providing the right type of stimulation, at the right intensity, with enough repetition to drive lasting neural change.</p>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Glowing%20human%20brain%20with%20neural%20connections%20and%20synapses%20firing%20colorful%20neurons%20network%20on%20dark%20blue%20background%20scientific%20medical%20illustration%20showing%20neuroplasticity%20and%20brain%20activity&width=600&height=400&seq=neuro007&orientation=landscape"
                  alt="Brain neuroplasticity"
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why It Matters Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-8">
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <p className="font-medium text-gray-900">Hope for Recovery</p>
                    <p className="text-sm text-gray-600 mt-1">Even after injury, the brain can form new pathways to regain lost functions.</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <p className="font-medium text-gray-900">Lifelong Potential</p>
                    <p className="text-sm text-gray-600 mt-1">Neuroplasticity occurs at any age — it&apos;s never too late to make positive changes.</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <p className="font-medium text-gray-900">Targeted Therapy</p>
                    <p className="text-sm text-gray-600 mt-1">Understanding plasticity principles allows us to design more effective rehabilitation.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-cyan-600 font-medium mb-2">Why It Matters</p>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Neuroplasticity Matters in Recovery</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Rehabilitation programs are designed to unlock your brain&apos;s potential for rewiring and recovery. By applying the principles of neuroplasticity, we can help you regain function and independence.
              </p>
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 transition-all cursor-pointer whitespace-nowrap">
                <span>Start Your Journey</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 10 Core Principles */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-medium mb-2">Key Principles</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">10 Core Principles</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These evidence-based principles guide how we structure our rehabilitation programs to maximize neuroplastic change.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {corePrinciples.map((principle, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <span className="text-2xl font-bold text-teal-500 mb-2 block">{String(index + 1).padStart(2, '0')}</span>
                <h4 className="font-semibold text-gray-900 text-sm mb-1">{principle.title}</h4>
                <p className="text-xs text-gray-500">{principle.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How We Apply Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-cyan-600 font-medium mb-2">Our Approach</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How We Apply Neuroplasticity</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We use advanced technologies that harness your brain&apos;s natural ability to rewire.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center p-6 bg-rose-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">Traumatic Brain Injury Recovery</h3>
              <p className="text-sm text-gray-600">Specialized protocols for TBI rehabilitation using neuroplasticity principles.</p>
            </div>
            <div className="text-center p-6 bg-teal-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">Advanced Tech</h3>
              <p className="text-sm text-gray-600">Cutting-edge rehabilitation technology to accelerate neural adaptation.</p>
            </div>
            <div className="text-center p-6 bg-amber-50 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-2">Personal Care</h3>
              <p className="text-sm text-gray-600">Customized rehabilitation plans tailored to your specific needs and goals.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Technologies Section */}
      <div className="py-16 bg-gradient-to-br from-gray-50 to-teal-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-teal-600 font-medium mb-2">Our Technology</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Technologies That Amplify Neuroplasticity</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Each of our technologies is specifically designed to provide the type of stimulation that drives neuroplastic change.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group"
              >
                <div className="h-40 overflow-hidden">
                  <img
                    src={tech.image}
                    alt={tech.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h4 className="font-semibold text-gray-900 mb-2">{tech.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{tech.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How This Translates Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-emerald-600 font-medium mb-2">In Practice</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How This Translates into Practice</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Evidence-based implementation of neuroplasticity principles in your daily rehabilitation sessions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {practiceAreas.map((area, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-teal-50/50 rounded-xl p-6 hover:shadow-md transition-all duration-300"
              >
                <h4 className="font-semibold text-gray-900 mb-2">{area.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{area.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Harness Your Brain&apos;s Potential?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Our expert team is ready to design a personalized rehabilitation program that leverages the power of neuroplasticity for your recovery.
          </p>
          <a
            href="mailto:enquire@archealth.net.au"
            className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line text-lg"></i>
            <span>Email Us</span>
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">© 2025 Accelerated Recovery Centre (ARC). All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
